import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { Loader2, CheckCircle, MailCheck } from 'lucide-react';
import Logo from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { authApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { loginWithToken } = useAuth();

  // Set by RegisterPage when it sends somebody here straight after signing up.
  //
  // ⚠️ THIS IS A HINT, NOT A FACT. Router state rides in the browser's history entry, so
  // it survives F5 but NOT a fresh tab, a pasted URL or a bookmark — and it is supplied by
  // the previous page, so it proves nothing about who is looking. It only ever chooses
  // which words to show. Nothing is unlocked by it, and the fallback below has to be a
  // complete answer on its own.
  const sentTo = location.state?.sentTo || null;

  // 'verifying' | 'success' | 'error' | 'sent' | 'notoken'
  const [status, setStatus] = useState(
    token ? 'verifying' : (sentTo ? 'sent' : 'notoken'));
  const [error, setError] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [resending, setResending] = useState(false);
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;   // guard against double-run in StrictMode
    (async () => {
      try {
        const res = await authApi.verifyEmail(token);
        loginWithToken(res.data);        // log straight in
        setStatus('success');
        toast.success('Email confirmed — welcome to HolidayHive!');
        setTimeout(() => navigate('/onboarding'), 1800);
      } catch (err) {
        setError(err.response?.data?.detail || 'This confirmation link is invalid or has expired.');
        setStatus('error');
      }
    })();
  }, [token, loginWithToken, navigate]);

  /** Resend to the address we already know, from the 'sent' state — no form to fill in. */
  const handleResendKnown = async () => {
    if (!sentTo) return;
    setResending(true);
    try {
      await authApi.resendVerification(sentTo);
      toast.success('Confirmation email sent again.');
    } catch (err) {
      toast.error('Could not resend right now — please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) return;
    setResending(true);
    try {
      await authApi.resendVerification(resendEmail);
      toast.success('If that account still needs confirming, a new link is on its way.');
    } catch (err) {
      toast.error('Could not resend right now — please try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-secondary/20 p-4">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center mb-8">
          <Logo size={34} />
        </div>

        <div className="bg-card border border-border rounded-xl p-8 shadow-lg text-center">
          {status === 'verifying' && (
            <div className="py-4">
              <Loader2 className="w-14 h-14 text-primary mx-auto mb-4 animate-spin" />
              <h1 className="text-2xl font-heading font-bold mb-2">Confirming your email…</h1>
              <p className="text-muted-foreground">Hang tight, this only takes a second.</p>
            </div>
          )}

          {status === 'success' && (
            <div className="py-4">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Email confirmed!</h1>
              <p className="text-muted-foreground mb-4">
                Your account is active. Taking you to setup…
              </p>
              <Link to="/onboarding">
                <Button className="btn-primary">Continue</Button>
              </Link>
            </div>
          )}

          {status === 'sent' && (
            <div className="py-4" data-testid="verify-sent">
              <MailCheck className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Confirm your email</h1>
              <p className="text-muted-foreground mb-6">
                We've sent a confirmation link to{' '}
                <span className="font-semibold text-foreground">{sentTo}</span>. Click it to
                activate your account — then you can log in.
              </p>
              <div className="rounded-lg border border-border bg-muted/40 p-4 text-sm text-muted-foreground mb-6 text-left">
                Didn't get it? Check your spam folder, or resend the link below. The link
                expires in 24 hours.
              </div>
              <Button
                type="button"
                onClick={handleResendKnown}
                variant="outline"
                className="w-full"
                disabled={resending}
                data-testid="verify-resend-btn"
              >
                {resending ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Resending…</>
                ) : (
                  'Resend confirmation email'
                )}
              </Button>
            </div>
          )}

          {status === 'notoken' && (
            <div className="py-4">
              {/* ⚠️ NOT "Invalid link" with a red cross any more. Whoever lands here has
                  usually just signed up and opened the page in a fresh tab, or come back
                  to it later — nothing has gone wrong, and telling them it has is both
                  inaccurate and alarming. The route out is the same either way: send
                  another link. */}
              <MailCheck className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Confirm your email</h1>
              <p className="text-muted-foreground mb-6">
                Enter the address you signed up with and we'll send a fresh link.
              </p>
              <ResendForm
                email={resendEmail}
                setEmail={setResendEmail}
                onSubmit={handleResend}
                resending={resending}
              />
            </div>
          )}

          {status === 'error' && (
            <div className="py-4">
              <MailCheck className="w-16 h-16 text-primary mx-auto mb-4" />
              <h1 className="text-2xl font-heading font-bold mb-2">Link expired or already used</h1>
              <p className="text-muted-foreground mb-6">{error}</p>
              <ResendForm
                email={resendEmail}
                setEmail={setResendEmail}
                onSubmit={handleResend}
                resending={resending}
              />
            </div>
          )}

          <p className="text-center text-muted-foreground mt-6 text-sm">
            <Link to="/login" className="text-primary hover:underline">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

const ResendForm = ({ email, setEmail, onSubmit, resending }) => (
  <form onSubmit={onSubmit} className="space-y-3 text-left">
    <div className="space-y-2">
      <Label htmlFor="resend-email">Email address</Label>
      <Input
        id="resend-email"
        type="email"
        placeholder="you@company.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="input-hive"
        required
      />
    </div>
    <Button type="submit" className="btn-primary w-full" disabled={resending}>
      {resending ? (
        <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending…</>
      ) : (
        'Send a new confirmation link'
      )}
    </Button>
  </form>
);

export default VerifyEmailPage;
